import { useState, useMemo } from "react";
import DescriptionBanner from '../Components/DescriptionBanner.jsx';
import CircularProgressRisk from "../Components/CircularProgressRisk.jsx";
import Tabs from "../Components/UI/Tabs.jsx";
import useGet from "../Hooks/useGet.jsx";
import API_ENDPOINTS from "../Config/api.js";
import { useNavigate } from "react-router-dom";
import EmptyStateFornitori from "../Components/EmptyStateFornitori.jsx";
import FornitoriAttiviTable from "../Components/Table/FornitoriAttiviTable.jsx";
import FornitoriNonAttiviTable from "../Components/Table/FornitorNonAttiviTable.jsx";
import ClipLoader from "react-spinners/ClipLoader.js";
import SearchBar from "../Components/UI/Searchbar.jsx";
import Selector from "../Components/UI/Selector.jsx";
import { Icon } from "../Components/UI/Icon.jsx";
import exportToCsv from "../Utils/exportToCsv.js";

export default function Dashboard() {

  const [tab, setTab] = useState("attivi");
  const [searchTerm, setSearchTerm] = useState("");        
  const [statusFilter, setStatusFilter] = useState("");   
  const navigate = useNavigate();

  const { fetchedData: aziendeMonitorateEnriched, refetch: refetchAttivi } = useGet(API_ENDPOINTS.aziendeMonitorateEnriched);
  const { fetchedData: aziendeCaricate, refetch: refetchNonAttivi } = useGet(API_ENDPOINTS.aziendeCaricate);
  const { fetchedData: statisticheAggregate } = useGet(API_ENDPOINTS.statisticheAggregate);

  const attiviData = useMemo(() => {
    const raw = aziendeMonitorateEnriched;
    return Array.isArray(raw) ? raw : (raw?.results ?? []);
  }, [aziendeMonitorateEnriched]);

  const nonAttiviData = useMemo(() => {
    const raw = aziendeCaricate;
    return Array.isArray(raw) ? raw : (raw?.results ?? []);
  }, [aziendeCaricate]);
  const isLoading = !aziendeMonitorateEnriched || !aziendeCaricate || !statisticheAggregate;

  const STATUS_OPTIONS = useMemo(() => ([
    { value: "", label: "Tutti" },
    { value: "da contattare", label: "Da contattare" },
    { value: "contattata", label: "Contattata" },
    { value: "rifiutato", label: "Rifiutata" },
    { value: "contrattualizzata", label: "Contrattualizzata" },
  ]), []);

  const COLUMNS_ATTIVI = useMemo(() => ([
    { header: "Ragione sociale", key: "ragione_sociale" },
    { header: "Partita IVA", key: "partita_iva" },
    { header: "Consenso", key: "consenso_condivisione", format: v => (v ? "SI" : "NO") },
    { header: "Cyber Risk (%)", key: "cyber_risk_percent", format: v => v == null ? "" : String(v) },
    { header: "NIS2 Risk (%)", key: "nis2_noncompliance_risk_percent", format: v => v == null ? "" : String(v) },
    { header: "Rischio aggregato (%)", key: "overall_risk_percent", format: v => v == null ? "" : String(v) },
  ]), []);

  const COLUMNS_NON_ATTIVI = useMemo(() => ([
    { header: "Ragione sociale", key: "denominazione" },
    { header: "Partita IVA", key: "partita_iva" },
    { header: "Stato contatto", key: "stato_contatto", format: (v) => (v ? String(v).toUpperCase() : "") },
    { header: "Email riferimento", key: "email_riferimento" },
    { header: "Email contatto", key: "email_contatto" },
    { header: "Codice fiscale", key: "codice_fiscale" },
    { header: "Telefono", key: "telefono" },
    { header: "Indirizzo", key: "indirizzo" },
    { header: "Dominio web", key: "dominio_web" },
    { header: "Nota", key: "nota" },
  ]), []);

  const filteredAttivi = useMemo(() => {
    return (attiviData ?? []).filter(it =>
      (it?.denominazione ?? "").toLowerCase().includes((searchTerm ?? "").toLowerCase())
    );
  }, [attiviData, searchTerm]);

  const filteredNonAttivi = useMemo(() => {
    return (nonAttiviData ?? []).filter(it => {
      const matchText = (it?.denominazione ?? "").toLowerCase().includes((searchTerm ?? "").toLowerCase());
      const matchStatus = !statusFilter || (it?.stato_contatto ?? "").toLowerCase() === statusFilter;
      return matchText && matchStatus;
    });
  }, [nonAttiviData, searchTerm, statusFilter]);

  const handleExportCsv = () => {
    const rows = tab === "attivi" ? filteredAttivi : filteredNonAttivi;
    const columns = tab === "attivi" ? COLUMNS_ATTIVI : COLUMNS_NON_ATTIVI;
    if (!rows?.length) return;
    exportToCsv(rows, {
      columns: columns,
      delimiter: ";",
      filename: tab === "attivi" ? "Fornitori Monitorati" : "Wishlist fornitori",
      excelSepHint: true,
      includeBom: true,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="flex flex-col items-center gap-4">
          <ClipLoader size={56} />
          <p className="text-sm text-gray-500">Caricamento dati dashboard…</p>
        </div>
      </div>
    );
  }

  if (attiviData.length === 0 && nonAttiviData.length === 0) {
    return (
      <div className="min-h-screen p-6">
        <EmptyStateFornitori />
      </div>
    );
  }

  const tabsData = [
    {
      label: 'FORNITORI MONITORATI',
      children: <FornitoriAttiviTable
        data={attiviData}
        refreshData={refetchAttivi}
        searchTerm={searchTerm}        
      />,
    },
    {
      label: 'WHISHLIST',
      children: <FornitoriNonAttiviTable
        data={nonAttiviData}
        refreshData={refetchNonAttivi}
        searchTerm={searchTerm}         
        statusFilter={statusFilter}      
      />,
    },
  ];

  return (
    <div className="min-h-screen space-y-10 p-6">
      <DescriptionBanner
        title="Dashboard"
        description="Fornisce una mappa generale della lista di fornitori censiti e di quelli sottoposti a monitoraggio…"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CircularProgressRisk
          data={[
            { label: "ALTO", value: statisticheAggregate?.metrics.cyber_risk_percent.distribution_percent.alto, color: "#DC2626" },
            { label: "MEDIO", value: statisticheAggregate?.metrics.cyber_risk_percent.distribution_percent.medio, color: "#F5A300" },
            { label: "BASSO", value: statisticheAggregate?.metrics.cyber_risk_percent.distribution_percent.basso, color: "#16A34A" },
          ]}
          title="STATO DELLA SUPPLY CHAIN"
          subtitle="Indicatore di rischio aggregato"
        />
        <CircularProgressRisk
          data={[
            { label: "ALTO", value: statisticheAggregate?.metrics.nis2_noncompliance_risk_percent.distribution_percent.alto, color: "#DC2626" },
            { label: "MEDIO", value: statisticheAggregate?.metrics.nis2_noncompliance_risk_percent.distribution_percent.medio, color: "#F5A300" },
            { label: "BASSO", value: statisticheAggregate?.metrics.nis2_noncompliance_risk_percent.distribution_percent.basso, color: "#16A34A" },
          ]}
          title="COMPLIANCE ALLA NIS2"
          subtitle="Indicatore di rischio aggregato"
        />
      </div>

      <div className="w-full h-[2px] bg-gray-300 my-2" />

      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-3">
            <div className="flex items-center gap-3">
              <SearchBar
                search={searchTerm}
                setSearch={setSearchTerm}
                placeholder="Cerca fornitori…"
              />
            </div>
            <button
              className="flex items-center gap-2 bg-white text-gray-800 shadow-md hover:shadow-lg transition-all duration-300 px-3 py-2 rounded-md active:scale-95"
              onClick={handleExportCsv}
            >
              <Icon name={'download'} />
              Esporta Csv
            </button>
          </div>
          <button
            onClick={() => navigate("/addnewfornitore")}
            className="flex items-center gap-2 bg-white text-gray-800 shadow-md hover:shadow-lg transition-all duration-300 px-3 py-2 rounded-md active:scale-95"
          >
            <Icon name={'plus'} size={18} />
            Aggiungi fornitore
          </button>
        </div>
        <Tabs tabs={tabsData} />
      </div>
    </div>
  );
}
